import { Category, Recommendation } from '@/types/assessment';

/**
 * Extract recommendations from specific answers between current and target values
 */
export const getAnswerRecommendations = (
  categories: Category[],
  responses: Record<string, {
    questionId: string;
    current: string[] | number;
    target: string[] | number;
  }>
): Recommendation[] => {
  const recommendations: Recommendation[] = [];
  
  // Process each question's responses
  Object.entries(responses).forEach(([questionId, response]) => {
    // Find the question and its category
    let questionFound = false;
    let categoryId = '';
    let categoryName = '';
    let question;
    
    for (const category of categories) {
      question = category.questions.find(q => q.id === questionId);
      if (question) {
        categoryId = category.id;
        categoryName = category.name;
        questionFound = true;
        break;
      }
    }
    
    if (!questionFound || !question) {
      return;
    }
    
    // For maturity questions (both 'maturity' and 'slider' types in maturity models)
    if (question.answerType === 'maturity' || question.answerType === 'slider') {
      // For maturity questions, we need to convert answer IDs to their numeric values
      let currentValue = 0;
      let targetValue = 0;
      
      if (typeof response.current === 'number') {
        currentValue = response.current;
      } else if (Array.isArray(response.current) && response.current.length > 0) {
        // Find the answer with the matching ID and get its value
        const currentAnswer = question.answers.find(a => a.id === response.current[0]);
        currentValue = currentAnswer ? currentAnswer.value : 0;
      }
      
      if (typeof response.target === 'number') {
        targetValue = response.target;
      } else if (Array.isArray(response.target) && response.target.length > 0) {
        // Find the answer with the matching ID and get its value
        const targetAnswer = question.answers.find(a => a.id === response.target[0]);
        targetValue = targetAnswer ? targetAnswer.value : 0;
      }
      
      if (targetValue <= currentValue) {
        return; // No recommendations needed
      }
      
      // Find answers between current and target values (inclusive of target, exclusive of current)
      const relevantAnswers = question.answers.filter(answer => {
        const isRelevant = answer.value > currentValue && answer.value <= targetValue;
        return isRelevant;
      });
      
      // Collect recommended actions from relevant answers
      relevantAnswers.forEach(answer => {
        if (answer.recommendedActions && answer.recommendedActions.length > 0) {
          answer.recommendedActions.forEach((action, actionIndex) => {
            const recommendation = {
              id: crypto.randomUUID(),
              categoryId,
              categoryName,
              questionId,
              questionText: question.text,
              text: action.text,
              priority: action.priority,
              gap: targetValue - currentValue,
              actionItems: [] // No additional action items for answer-based recommendations
            };
            recommendations.push(recommendation);
          });
        }
      });
    } 
    // For single or multiple choice questions
    else {
      const currentAnswerIds = Array.isArray(response.current) ? response.current : [];
      const targetAnswerIds = Array.isArray(response.target) ? response.target : [];
      
      // Find answers that are in target but not in current
      const newTargetAnswerIds = targetAnswerIds.filter(id => !currentAnswerIds.includes(id));
      
      // Collect recommended actions from new target answers
      newTargetAnswerIds.forEach(answerId => {
        const answer = question.answers.find(a => a.id === answerId);
        
        if (answer) {
          if (answer.recommendedActions && answer.recommendedActions.length > 0) {
            answer.recommendedActions.forEach((action, actionIndex) => {
              const recommendation = {
                id: crypto.randomUUID(),
                categoryId,
                categoryName,
                questionId,
                questionText: question.text,
                text: action.text,
                priority: action.priority,
                gap: 1, // Default gap for choice questions
                actionItems: [] // No additional action items for answer-based recommendations
              };
              recommendations.push(recommendation);
            });
          }
        }
      });
    }
  });
  
  return recommendations;
};
